import React from "react";
import { Loader2, Check } from "lucide-react";
import { formatDateCompact } from "@/lib/date_format";
import { todayISO } from "./mobileActualValidation";
import { cn } from "@/lib/utils";

/**
 * MobileActualDateCell — a CONTROLLED, presentational cell for ONE Actual date
 * (start or finish) in the mobile Collaborator table. The parent owns `value`
 * and `saveState`; this component NEVER calls an API.
 *
 * Native <input type="date"> is used on purpose so the mobile OS shows its own
 * date picker (a custom JS calendar would defeat that). `min`/`max` pass through
 * to bound the native picker — `max` is the project data_date.
 *
 * Phase-2 prop contract:
 *   value      string "YYYY-MM-DD" | null   — current actual date
 *   editable   bool                          — collaborate+ on the project
 *   disabled   bool                          — temporarily non-interactive
 *                                              (e.g. Finish with no Start yet)
 *   label      string                        — a11y label for the input/Today
 *   onChange   (newValueOrNull) => void      — fired on native change + Today
 *   onCommit?  (value) => void               — optional; fired once per pick (on
 *                                              `change`/Today) so the parent saves.
 *                                              NOT fired on blur (would double-save).
 *   saveState  'idle'|'saving'|'saved'|'error'
 *   errorText  string | null                 — shown when saveState === 'error'
 *   min        "YYYY-MM-DD" | null           — native picker lower bound
 *   max        "YYYY-MM-DD" | null           — native picker upper bound (data_date)
 */
function MobileActualDateCell({
  value,
  editable = false,
  disabled = false,
  label,
  onChange,
  onCommit,
  saveState = "idle",
  errorText = null,
  min = null,
  max = null,
}) {
  // Read-only path: muted compact text, no input affordance. This is how a
  // Viewer (or a read-only column) sees the cell.
  if (!editable || disabled) {
    return (
      <div
        className="flex items-center justify-between gap-1 min-h-[44px] px-2 text-sm text-slate-400"
        data-testid="mobile-actual-cell"
        data-readonly="true"
        aria-label={label}
      >
        <span className="tabular-nums">{formatDateCompact(value)}</span>
      </div>
    );
  }

  const isSaving = saveState === "saving";
  const isSaved = saveState === "saved";
  const isError = saveState === "error";

  const emit = (next) => {
    const normalized = next || null; // "" -> null (cleared)
    onChange?.(normalized);
  };

  // Native <input type="date"> commits a full value on `change` (when the OS
  // picker confirms / a complete date is entered). We deliberately do NOT also
  // commit on `blur`: doing both fires the save twice for one pick (the picker
  // closing blurs the input), causing duplicate PUTs and a save-state race.
  const handleChange = (e) => {
    const next = e.target.value || null;
    emit(next);
    onCommit?.(next);
  };

  const handleToday = () => {
    const t = todayISO();
    onChange?.(t);
    onCommit?.(t);
  };

  return (
    <div
      className="flex flex-col gap-1 px-1 py-1"
      data-testid="mobile-actual-cell"
      data-readonly="false"
    >
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={value || ""}
          min={min || undefined}
          max={max || undefined}
          disabled={isSaving}
          onChange={handleChange}
          aria-label={label}
          aria-invalid={isError || undefined}
          data-testid="mobile-actual-input"
          className={cn(
            "h-11 flex-1 min-w-0 rounded-md border bg-white px-2 text-sm",
            "focus:outline-none focus:ring-2 focus:ring-slate-400",
            isError ? "border-red-500 ring-1 ring-red-500" : "border-slate-300",
            isSaving && "opacity-60",
          )}
        />

        <button
          type="button"
          onClick={handleToday}
          disabled={isSaving}
          aria-label={`Set ${label || "actual date"} to today`}
          data-testid="mobile-actual-today-btn"
          className={cn(
            "h-11 shrink-0 rounded-md border border-slate-300 bg-white px-2",
            "text-xs font-medium text-slate-700 active:bg-slate-100",
            "disabled:opacity-50",
          )}
        >
          Today
        </button>

        {/* Save-state affordance: spinner while saving, emerald check briefly
            on success (a clear written + iconographic confirmation). */}
        <span
          className="flex w-5 shrink-0 items-center justify-center"
          data-testid="mobile-actual-savestate"
          aria-live="polite"
        >
          {isSaving && (
            <Loader2
              className="h-4 w-4 animate-spin text-slate-400"
              data-testid="mobile-actual-spinner"
              aria-label="Saving"
            />
          )}
          {isSaved && (
            <Check
              className="h-4 w-4 text-emerald-600"
              data-testid="mobile-actual-saved"
              aria-label="Saved"
            />
          )}
        </span>
      </div>

      {isSaved && (
        <span className="px-1 text-xs text-emerald-600" data-testid="mobile-actual-saved-text">
          Saved
        </span>
      )}

      {isError && errorText && (
        <span
          className="px-1 text-xs text-red-600"
          role="alert"
          data-testid="mobile-actual-error-text"
        >
          {errorText}
        </span>
      )}
    </div>
  );
}

export default React.memo(MobileActualDateCell);
